import { useState, useEffect } from "react";
import styles from "./CreateLiveSessionPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import useDashboardStore from "../../store/dashboardStore";
import useAuthStore from "../../store/authStore";
import CustomDatePicker from "../../components/Calendar/CustomDatePicker";
import VideoPopup from "../../components/VideoPopup/VideoPopup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { decryptPassword } from "../../utils/crypto";

const CreateLiveSessionPage = () => {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.roles[0] === "administrator";

  // Left Section Details
  const [sessionName, setSessionName] = useState("");
  const { setIsAllLiveSessionScreen, communities, editLiveSession } =
    useDashboardStore();
  const [selectedCommunityId, setSelectedCommunityId] = useState(null);
  const [isUs30, setIsUs30] = useState(false);
  const [date, setDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]); // NEW: For multi-select in create mode
  const [showPopup, setShowPopup] = useState(false);

  // Right Section Details
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [zoomLink, setZoomLink] = useState("");
  const [zoomPassword, setZoomPassword] = useState("");
  const [isSessionPaid, setIsSessionPaid] = useState(false);
  const [membershipLink, setMembershipLink] = useState("");
  const [memberships, setMemberships] = useState([]);
  const [selectedMembershipIds, setSelectedMembershipIds] = useState([]);
  const [searchTextMembership, setSearchTextMembership] = useState("");
  const [videos, setVideos] = useState([]);

  // Traders list and selected instructor
  const [traders, setTraders] = useState([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState(null);

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Select/Deselect Membership
  const handleMembershipChange = (membership) => {
    setSelectedMembershipIds((prev) =>
      prev.includes(membership.id)
        ? prev.filter((s) => s !== membership.id)
        : [...prev, membership.id]
    );
  };

  // Change Date - handle both single and multi-select
  const handleDateChange = (dateOrDates) => {
    if (editLiveSession !== null) {
      // Update mode: single date
      setDate(dateOrDates);
    } else {
      // Create mode: multiple dates
      setSelectedDates(dateOrDates);
    }
  };

  // Videos
  const handlePopupSubmit = (newVideos) => {
    setVideos(newVideos);
  };

  // Create Live Session
  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Validation for create mode
    if (editLiveSession === null) {
      if (
        !sessionName ||
        selectedDates.length === 0 ||
        !startTime ||
        !endTime ||
        !zoomLink ||
        !zoomPassword
      ) {
        toast.error("Please fill all fields and select at least one date.");
        return;
      }
    } else {
      // Validation for update mode
      if (
        !sessionName ||
        !date ||
        !startTime ||
        !endTime ||
        !zoomLink ||
        !zoomPassword
      ) {
        toast.error("Please fill all fields.");
        return;
      }
    }

    if (isUs30 === false && !selectedCommunityId) {
      toast.error("Please select category.");
      return;
    }

    // Admin must select an instructor for non-US30 sessions
    if (isAdmin && !isUs30 && !selectedInstructorId) {
      toast.error("Please select an instructor.");
      return;
    }

    if (isSessionPaid === true) {
      if (!selectedMembershipIds.length) {
        toast.error("Please fill membership details");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Determine instructor_id based on session type
      let instructorId;
      if (isUs30) {
        // US30 sessions are always assigned to the admin
        instructorId = user.id;
      } else {
        // Regular sessions: use selected instructor
        instructorId = selectedInstructorId;
      }

      if (editLiveSession !== null) {
        // UPDATE MODE: Single date
        const baseDate = dayjs(date);
        let start = dayjs(`${baseDate.format("YYYY-MM-DD")}T${startTime}:00`);
        let end = dayjs(`${baseDate.format("YYYY-MM-DD")}T${endTime}:00`);

        if (end.isBefore(start)) {
          end = end.add(1, "day");
        }

        const payload = {
          session_id: editLiveSession.id,
          session_title: sessionName,
          start_datetime: start.utc().format("YYYY-MM-DDTHH:mm:ss"),
          end_datetime: end.utc().format("YYYY-MM-DDTHH:mm:ss"),
          zoom_link: zoomLink.trim(),
          zoom_password: zoomPassword,
          is_us30: isUs30,
          is_paid: isSessionPaid,
          membership_link: membershipLink.trim(),
          membership_ids: selectedMembershipIds,
          videos: isUs30 === true ? videos : [],
          community_id: selectedCommunityId ?? communities[0]?.id,
          instructor_id: instructorId,
        };

        const { data } = await api.put(ENDPOINTS.UPDATE_LIVE_SESSION, payload);

        if (data.success) {
          toast.success(data.message || "Live session updated successfully");
          setIsAllLiveSessionScreen();
        } else {
          toast.error(data.message || "Something went wrong");
        }
      } else {
        // CREATE MODE: Multiple dates
        const datesToCreate = selectedDates.map((dateStr) => {
          const baseDate = dayjs(dateStr);
          let start = dayjs(`${baseDate.format("YYYY-MM-DD")}T${startTime}:00`);
          let end = dayjs(`${baseDate.format("YYYY-MM-DD")}T${endTime}:00`);

          if (end.isBefore(start)) {
            end = end.add(1, "day");
          }

          return start.utc().format("YYYY-MM-DDTHH:mm:ss");
        });

        // Calculate end datetime based on first date
        const firstBaseDate = dayjs(selectedDates[0]);
        let firstStart = dayjs(
          `${firstBaseDate.format("YYYY-MM-DD")}T${startTime}:00`
        );
        let firstEnd = dayjs(
          `${firstBaseDate.format("YYYY-MM-DD")}T${endTime}:00`
        );

        if (firstEnd.isBefore(firstStart)) {
          firstEnd = firstEnd.add(1, "day");
        }

        const payload = {
          session_title: sessionName,
          start_datetime: firstStart.utc().format("YYYY-MM-DDTHH:mm:ss"),
          end_datetime: firstEnd.utc().format("YYYY-MM-DDTHH:mm:ss"),
          zoom_link: zoomLink.trim(),
          zoom_password: zoomPassword,
          is_us30: isUs30,
          is_paid: isSessionPaid,
          membership_link: membershipLink.trim(),
          membership_ids: selectedMembershipIds,
          videos: isUs30 === true ? videos : [],
          community_id: selectedCommunityId ?? communities[0]?.id,
          instructor_id: instructorId,
          selected_dates: datesToCreate, // NEW: Pass all selected dates
        };

        const { data } = await api.post(ENDPOINTS.CREATE_LIVE_SESSION, payload);

        if (data.success) {
          toast.success(
            data.message || `${data.count} live session(s) created successfully`
          );
          setIsAllLiveSessionScreen();
        } else {
          toast.error(data.message || "Something went wrong");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Error creating session");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize Data
  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        if (data.success) {
          setMemberships(data.memberships);
        }
      } catch (err) {
        console.error("Failed to fetch memberships", err);
      }
    };

    const fetchTraders = async () => {
      if (!isAdmin) return;

      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_TRADERS_SIMPLE);
        if (data.success) {
          setTraders(data.traders);
        }
      } catch (err) {
        console.error("Failed to fetch traders", err);
      }
    };

    fetchMemberships();
    fetchTraders();
  }, [isAdmin]);

  useEffect(() => {
    const resetForm = () => {
      setSessionName("");
      setSelectedCommunityId(null);
      setIsUs30(false);
      setDate(null);
      setSelectedDates([]); // NEW: Reset selected dates
      setStartTime("");
      setEndTime("");
      setZoomLink("");
      setZoomPassword("");
      setIsSessionPaid(false);
      setMembershipLink("");
      setSelectedMembershipIds([]);
      setVideos([]);
      setSelectedInstructorId(null);
    };

    const prefillFormForEdit = async () => {
      setSessionName(editLiveSession.session_title);
      if (editLiveSession.is_us30 === 1) {
        setSelectedCommunityId(null);
        setIsUs30(true);
      } else {
        setSelectedCommunityId(editLiveSession.community_id);
        setIsUs30(false);
      }
      setZoomLink(editLiveSession.zoom_link);
      setZoomPassword(decryptPassword(editLiveSession.zoom_password));
      setIsSessionPaid(editLiveSession.is_paid === 1 ? true : false);
      setMembershipLink(editLiveSession.membership_link);
      setSelectedMembershipIds(editLiveSession.membership_ids);
      setVideos(editLiveSession.videos);
      setSelectedInstructorId(editLiveSession.instructor_id);

      const start = new Date(editLiveSession.start_datetime);
      const end = new Date(editLiveSession.end_datetime);
      const localDate = start.toLocaleDateString("en-CA");

      const startTime = start.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      const endTime = end.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      setDate(localDate);
      setStartTime(startTime);
      setEndTime(endTime);
    };

    if (editLiveSession !== null) {
      prefillFormForEdit();
    } else if (editLiveSession === null) {
      resetForm();
    }
  }, [editLiveSession]);

  const filteredMemberships = memberships.filter((membership) =>
    membership.label.toLowerCase().includes(searchTextMembership.toLowerCase())
  );

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editLiveSession === null ? "Create" : "Update"} Live Session
        </h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Session Title */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Session Title</label>
            <input
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Select Community */}
          <div className={styles.sectionsWrapper}>
            <label className={styles.inlineLabel}>Select Category</label>
            <div className={styles.sectionsCard}>
              <div className={styles.sectionList}>
                {/* Only Admin Can Create US30 Meetings */}
                {isAdmin && (
                  <div className={styles.sectionRow}>
                    <span className={styles.sectionLabel}>US 30</span>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={isUs30 === true}
                      disabled={isSubmitting}
                      onChange={() => {
                        setSelectedCommunityId(null);
                        setIsUs30(true);
                      }}
                    />
                  </div>
                )}
                {communities.map((community, i) => (
                  <div className={styles.sectionRow} key={i}>
                    <span className={styles.sectionLabel}>
                      {community.name}
                    </span>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={selectedCommunityId === community.id}
                      disabled={isSubmitting}
                      onChange={() => {
                        setSelectedCommunityId(community.id);
                        setIsUs30(false);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar Date Picker - Multi-select for create, single for update */}
          <CustomDatePicker
            preSelectedDate={date}
            selectedDates={selectedDates}
            multiSelect={editLiveSession === null} // Multi-select only in create mode
            onDateSelect={handleDateChange}
            disabled={isSubmitting}
          />

          {/* Show selected dates count in create mode */}
          {editLiveSession === null && selectedDates.length > 0 && (
            <div className={styles.selectedDatesInfo}>
              <span className={styles.selectedDatesText}>
                Creating {selectedDates.length} session
                {selectedDates.length !== 1 ? "s" : ""} with the same details
              </span>
            </div>
          )}

          {/* Add Training Videos */}
          {isUs30 === true && (
            <button
              className={styles.submitButton}
              onClick={() => setShowPopup(true)}
              disabled={isSubmitting}
            >
              <span>Add Training Videos</span>
              <span className={styles.submitIcon}>＋</span>
            </button>
          )}

          {showPopup && (
            <VideoPopup
              initialVideos={videos}
              onClose={() => setShowPopup(false)}
              onSubmit={handlePopupSubmit}
            />
          )}
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          {/* Start Time */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>
          {/* End Time */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>
          {/* Instructor - Dropdown for non-US30, locked for US30 */}
          {isAdmin && !isUs30 ? (
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Assign Instructor</label>
              <select
                value={selectedInstructorId || ""}
                onChange={(e) =>
                  setSelectedInstructorId(Number(e.target.value))
                }
                className={styles.textInput}
                disabled={isSubmitting}
              >
                <option value="">Select Trader...</option>
                {traders.map((trader) => (
                  <option key={trader.id} value={trader.id}>
                    {trader.username}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Instructor</label>
              <input
                type="text"
                disabled={true}
                value={`${user.first_name} ${user.last_name}`}
                className={styles.textInput}
              />
            </div>
          )}
          {/* Zoom ID */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Zoom Meeting Id</label>
            <input
              type="text"
              value={zoomLink}
              onChange={(e) => setZoomLink(e.target.value.replace(/\s/g, ""))}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>
          {/* Zoom Password */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Zoom Password</label>
            <input
              type="text"
              value={zoomPassword}
              onChange={(e) => setZoomPassword(e.target.value)}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>
          {/* Select Session Type */}
          <div className={styles.liveSessionRow}>
            <label className={styles.inlineLabel}>Session Type</label>

            <div className={styles.toggleWrapper}>
              <button
                className={`${styles.toggleOption} ${
                  !isSessionPaid ? styles.activeToggle : ""
                }`}
                onClick={() => setIsSessionPaid(false)}
                disabled={isSubmitting}
              >
                Free
              </button>
              <button
                className={`${styles.toggleOption} ${
                  isSessionPaid ? styles.activeToggle : ""
                }`}
                onClick={() => setIsSessionPaid(true)}
                disabled={isSubmitting}
              >
                Paid
              </button>
            </div>
            {/* <span className={styles.note}>
              <span className={styles.noteHighlight}>NB*</span>: Place below the
              link URL of the membership payment
              <br />
            </span> */}
          </div>
          {/* Membership link
          {isSessionPaid && (
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Membership Link</label>
              <input
                type="text"
                value={membershipLink}
                onChange={(e) => setMembershipLink(e.target.value)}
                className={styles.textInput}
                disabled={isSubmitting}
              />
            </div>
          )} */}
          {/* Select Memberships */}
          {isSessionPaid && (
            <div className={styles.sectionsWrapper}>
              <label className={styles.inlineLabel}>Memberships List</label>
              <div className={styles.sectionsCard}>
                <div className={styles.searchBar}>
                  <input
                    type="text"
                    placeholder="type in to Filter memberships"
                    className={styles.searchInput}
                    value={searchTextMembership}
                    onChange={(e) => setSearchTextMembership(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <span className={styles.searchIcon}>🔍</span>
                </div>

                <div className={styles.sectionList}>
                  {filteredMemberships.map((membership, i) => (
                    <div className={styles.sectionRow} key={i}>
                      <span className={styles.sectionLabel}>
                        {membership.label}
                      </span>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedMembershipIds.includes(membership.id)}
                        onChange={() => handleMembershipChange(membership)}
                        disabled={isSubmitting}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${
              isSubmitting ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span>
                  {editLiveSession === null ? "Creating..." : "Updating..."}
                </span>
                <span className={styles.submitIcon}>⟳</span>
              </>
            ) : (
              <>
                <span>Submit Session</span>
                <span className={styles.submitIcon}>＋</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLiveSessionPage;
